import "zod/compile";
import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  reportColorPaletteColorsSchema,
  reportColorPaletteNameSchema,
  reportColorPaletteSchema,
} from "@erp/shared-types";

const paletteId = z.object({ paletteId: z.uuid() });

/**
 * An organization's Report color palettes (Settings > Report Palettes). Any
 * member can list them, since every Report draws with one; creating, editing,
 * deleting and picking the default need OrganizationConfig Update.
 */
export const reportColorPalettesContracts = {
  /** The built-in palette first, then the organization's by name; exactly one is the default. */
  list: oc.input(z.object({})).output(z.array(reportColorPaletteSchema)).route({
    summary: "List report color palettes",
    description:
      "Lists the built-in palette and the organization's custom report color palettes by name, with their colors and which one is the default.",
    method: "GET",
  }),
  create: oc
    .input(
      z.object({
        name: reportColorPaletteNameSchema,
        colors: reportColorPaletteColorsSchema,
      }),
    )
    .output(reportColorPaletteSchema)
    .route({
      summary: "Create a report color palette",
      description:
        "Admin-only. Adds a named custom color palette that reports can draw charts with. Returns the new palette.",
    }),
  update: oc
    .input(
      paletteId.extend({
        name: reportColorPaletteNameSchema,
        colors: reportColorPaletteColorsSchema,
      }),
    )
    .output(reportColorPaletteSchema)
    .route({
      summary: "Update a report color palette",
      description:
        "Admin-only. Changes a custom report color palette's name and colors. Returns the updated palette.",
    }),
  /** Reports naming a deleted palette draw with the organization's default. */
  delete: oc
    .input(paletteId)
    .output(z.object({ success: z.literal(true) }))
    .route({
      summary: "Delete a report color palette",
      description:
        "Admin-only. Removes a custom report color palette; reports that used it fall back to the organization's default palette.",
    }),
  /** A palette's id, or the built-in palette's (`gently`). */
  setDefault: oc
    .input(z.object({ paletteId: z.string().min(1) }))
    .output(z.array(reportColorPaletteSchema))
    .route({
      summary: "Set the default report color palette",
      description:
        "Admin-only. Makes a custom palette, or the built-in one, the organization's default for reports. Returns all palettes.",
    }),
};
